import React, { useEffect, useMemo, useState } from "react";
import { usePagos } from "../state/PagosContext";
import { api } from "../api";

function catLabel(c) {
  const parts = [c.level1, c.level2, c.level3, c.name].filter(Boolean);
  return parts.join(" / ");
}

export default function Captura() {
  const {
    categorias,
    cuentasPagoObj,
    cuentasDestinoObj,
    valuadores,
    catalogo,
    addPago,
    session,
  } = usePagos();

  const role = session?.user?.role || "";
  const canCreate = role === "admin" || role === "captura";

  const [form, setForm] = useState({
    responsable: "Worth",
    nombre: "",
    fecha: new Date().toISOString().slice(0, 10),
    monto: "",
    clasificacion: "Nómina",
    ordenServicio: "",
    catalog_account_id: "",
    payment_account_id: "",
    destination_account_id: "",
    valuador_id: "",
  });

  const [msg, setMsg] = useState({ type: "", text: "" });
  const [valuadorCuentas, setValuadorCuentas] = useState({ out: [], in: [] });

  const isWorth = form.responsable === "Worth";
  const isNominaWorth = isWorth && (form.clasificacion || "").toLowerCase() === "nómina";


  const catsResp = useMemo(() => (categorias[form.responsable] || []), [categorias, form.responsable]);
  const cuentasPagoResp = useMemo(() => {
    const base = (cuentasPagoObj[form.responsable] || []);
    if (isNominaWorth && valuadorCuentas.out?.length) return base.filter((a)=> valuadorCuentas.out.includes(a.id));
    return base;
  }, [cuentasPagoObj, form.responsable, isNominaWorth, valuadorCuentas]);
  const cuentasDestinoResp = useMemo(() => {
    const base = (cuentasDestinoObj[form.responsable] || []);
    if (isNominaWorth && valuadorCuentas.in?.length) return base.filter((a)=> valuadorCuentas.in.includes(a.id));
    return base;
  }, [cuentasDestinoObj, form.responsable, isNominaWorth, valuadorCuentas]);

  const scope = form.responsable === "JPGM" ? "jpgm" : "worth";
  const catalogOptions = useMemo(() => {
    return catalogo
      .filter((c) => c.type === 'expense' && (c.scope === scope || c.scope === 'both'))
      .map((c) => ({ id: c.id, label: catLabel(c) }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [catalogo, scope]);

  function onChange(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  function validate() {
    if (!canCreate) return "Tu perfil no permite capturar gastos.";
    if (!form.nombre) return "Falta el nombre del gasto.";
    if (!form.fecha) return "Falta la fecha.";
    if (!form.monto || Number(form.monto) <= 0) return "Monto inválido.";
    if (!form.catalog_account_id) return "Selecciona la cuenta del catálogo.";
    if (!form.clasificacion) return "Selecciona la clasificación.";
    if (!form.payment_account_id) return "Selecciona la cuenta de pago (origen).";
    if (!form.destination_account_id) return "Selecciona la cuenta destino (beneficiario).";

    if (isNominaWorth && !form.valuador_id) return "Selecciona el valuador (Nómina).";

    return null;
  }

  async function onSubmit(e) {
    e.preventDefault();
    const err = validate();
    if (err) {
      setMsg({ type: "error", text: err });
      return;
    }

    try {
      await addPago({
        responsable: form.responsable,
        nombre: form.nombre,
        fecha: form.fecha,
        monto: Number(form.monto),
        clasificacion: form.clasificacion,
        ordenServicio: form.responsable === "Worth" ? (form.ordenServicio || "") : "",
        catalog_account_id: Number(form.catalog_account_id),
        payment_account_id: Number(form.payment_account_id),
        destination_account_id: Number(form.destination_account_id),
        valuador_id: form.valuador_id ? Number(form.valuador_id) : null,
      });

      setMsg({ type: "ok", text: "Gasto agregado." });
      setForm((f) => ({
        ...f,
        nombre: "",
        monto: "",
        ordenServicio: "",
      }));
    } catch (e2) {
      setMsg({ type: "error", text: "No se pudo guardar el gasto." });
    }
  }


  useEffect(() => {
    (async () => {
      if (!isNominaWorth || !form.valuador_id) { setValuadorCuentas({ out: [], in: [] }); return; }
      try {
        const r = await api.getValuadorCuentas(Number(form.valuador_id));
        setValuadorCuentas({ out: r.out || [], in: r.in || [] });
      } catch {
        setValuadorCuentas({ out: [], in: [] });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNominaWorth, form.valuador_id]);

  return (
    <div className="max-w-3xl mx-auto py-8 px-4">
      <div className="bg-white rounded-2xl shadow p-6 border-t-4 border-worthgold">
        <h1 className="text-2xl font-extrabold text-worthblue mb-4">Captura de Gasto</h1>

        {!canCreate && (
          <div className="mb-4 p-3 rounded bg-yellow-50 text-yellow-800 text-sm">
            Tu perfil (<b>{role}</b>) no permite capturar gastos.
          </div>
        )}

        {msg.text && (
          <div className={`mb-4 p-3 rounded ${msg.type === "ok" ? "bg-green-50 text-green-800" : "bg-red-50 text-red-700"}`}>
            {msg.text}
          </div>
        )}

        <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm text-gray-600">Responsable</label>
            <select className="w-full border rounded px-3 py-2" value={form.responsable} onChange={(e) => onChange("responsable", e.target.value)} disabled={!canCreate}>
              <option>Worth</option>
              <option>JPGM</option>
            </select>
          </div>

          <div>
            <label className="text-sm text-gray-600">Fecha</label>
            <input type="date" className="w-full border rounded px-3 py-2" value={form.fecha} onChange={(e) => onChange("fecha", e.target.value)} disabled={!canCreate} />
          </div>

          <div className="md:col-span-2">
            <label className="text-sm text-gray-600">Nombre del gasto</label>
            <input className="w-full border rounded px-3 py-2" placeholder="Ej. Nómina Valuador" value={form.nombre} onChange={(e) => onChange("nombre", e.target.value)} disabled={!canCreate} />
          </div>

          <div>
            <label className="text-sm text-gray-600">Monto</label>
            <input type="number" className="w-full border rounded px-3 py-2" value={form.monto} onChange={(e) => onChange("monto", e.target.value)} disabled={!canCreate} />
          </div>

          <div>
            <label className="text-sm text-gray-600">Clasificación</label>
            <select className="w-full border rounded px-3 py-2" value={form.clasificacion} onChange={(e) => onChange("clasificacion", e.target.value)} disabled={!canCreate}>
              {catsResp.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>

          <div className="md:col-span-2">
            <label className="text-sm text-gray-600">Cuenta (Catálogo)</label>
            <select className="w-full border rounded px-3 py-2" value={form.catalog_account_id} onChange={(e) => onChange("catalog_account_id", e.target.value)} disabled={!canCreate}>
              <option value="">Selecciona...</option>
              {catalogOptions.map((o) => (
                <option key={o.id} value={o.id}>{o.label}</option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">Catálogo cargado desde la base de datos (documento actualizado).</p>
          </div>

          {isNominaWorth && (
            <div className="md:col-span-2">
              <label className="text-sm text-gray-600">Valuador (Nómina Worth)</label>
              <select className="w-full border rounded px-3 py-2" value={form.valuador_id} onChange={(e) => onChange("valuador_id", e.target.value)} disabled={!canCreate}>
                <option value="">Selecciona...</option>
                {valuadores.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
            </div>
          )}

          {isWorth && (
            <div className="md:col-span-2">
              <label className="text-sm text-gray-600">Orden de servicio (Worth)</label>
              <input className="w-full border rounded px-3 py-2" value={form.ordenServicio} onChange={(e) => onChange("ordenServicio", e.target.value)} disabled={!canCreate} />
            </div>
          )}

          <div>
            <label className="text-sm text-gray-600">Cuenta de pago (origen)</label>
            <select className="w-full border rounded px-3 py-2" value={form.payment_account_id} onChange={(e) => onChange("payment_account_id", e.target.value)} disabled={!canCreate}>
              <option value="">Selecciona...</option>
              {cuentasPagoResp.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>

          <div>
            <label className="text-sm text-gray-600">Cuenta destino (beneficiario)</label>
            <select className="w-full border rounded px-3 py-2" value={form.destination_account_id} onChange={(e) => onChange("destination_account_id", e.target.value)} disabled={!canCreate}>
              <option value="">Selecciona...</option>
              {cuentasDestinoResp.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>

          <div className="md:col-span-2">
            <button className="bg-worthblue text-white px-4 py-2 rounded font-semibold hover:bg-worthgold disabled:opacity-60" type="submit" disabled={!canCreate}>
              Agregar gasto
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
